import { Check, ChevronLeft, ChevronRight } from 'lucide-react'
import type { MDXMetadata } from 'mdx/types'
import { Link, useLocation } from '#router'
import { cn } from '@/lib/utils'
import { Button } from './ui/button'

function* flattenPages(node: MDXMetadata): Generator<MDXMetadata> {
  yield node
  if (!node.children?.length) return
  for (const child of node.children) yield* flattenPages(child)
}

function findAdjacentPages(course: MDXMetadata, currentPage: MDXMetadata) {
  const allPages = Array.from(flattenPages(course))
  const currentIndex = allPages.findIndex((p) => p.href === currentPage.href)

  if (currentIndex === -1) return { previous: undefined, next: undefined }

  return {
    previous: allPages[currentIndex - 1],
    next: allPages[currentIndex + 1],
  }
}

export default function PreviousNext({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  const { course, page } = useLocation()
  if (!course || !page) return null
  const { previous, next } = findAdjacentPages(course, page)

  return (
    <div className={cn('flex gap-4', className)} {...props}>
      {previous ? (
        <Button
          variant="ghost"
          className="h-auto w-1/2 flex-1 justify-start overflow-hidden p-4 shadow-none"
          asChild
        >
          <Link href={previous.href}>
            <ChevronLeft className="mr-2" />
            <div className="flex flex-col">
              <span className="text-muted-foreground text-xs">Previous</span>
              <span className="font-medium text-wrap">{previous.title}</span>
            </div>
          </Link>
        </Button>
      ) : (
        <div className="w-1/2 flex-1" />
      )}
      {next ? (
        <Button
          variant="ghost"
          className="h-auto w-1/2 flex-1 justify-end overflow-hidden p-4 shadow-none"
          asChild
        >
          <Link href={next.href}>
            <div className="flex flex-col">
              <span className="text-muted-foreground text-xs">Next</span>
              <span className="font-medium text-wrap">{next.title}</span>
            </div>
            <ChevronRight className="ml-2" />
          </Link>
        </Button>
      ) : (
        <Button
          variant="ghost"
          className="h-auto w-1/2 flex-1 justify-start overflow-hidden p-4 shadow-none hover:bg-green-50 dark:hover:bg-green-950/30"
          asChild
        >
          <Link href="/courses">
            <Check className="mr-2 text-green-600" />
            <div className="flex flex-col">
              <span className="text-muted-foreground text-xs">Congrats!</span>
              <span className="font-medium text-wrap">Back to Courses</span>
            </div>
          </Link>
        </Button>
      )}
    </div>
  )
}
